import React from "react";
import ReactDOM from "react-dom";
import BlocksSelector from "./BlocksSelector";
import { useDispatch } from "react-redux";
import { setBlockRange } from "../../store/state/BlockSuggestion";
import { X } from "lucide-react";
import { ReactComponent as SelectBlock } from "../../icons/Select_block.svg";

export const TimeDivider = [
  {
    name: "Off-Peak",
    range: "12:00 AM – 6:00 AM",
    startBlock: 1,
    endBlock: 24,
  },
  { name: "Daytime", range: "6:00 AM – 6:00 PM", startBlock: 25, endBlock: 72 },
  {
    name: "Evening + Late Night",
    range: "6:00 PM – 12:00 AM",
    startBlock: 73,
    endBlock: 96,
  },
];

export const BlockSuggestions = [
  {
    label: "Morning Peak",
    range: "6:00 AM – 10:00 AM",
    startBlock: 25,
    endBlock: 40,
  },
  {
    label: "Midday Dip",
    range: "12:00 PM – 2:00 PM",
    startBlock: 49,
    endBlock: 56,
  },
  {
    label: "Evening Peak",
    range: "6:00 PM – 10:00 PM",
    startBlock: 73,
    endBlock: 88,
  },
  {
    label: "Off-Peak Night",
    range: "2:00 AM – 6:00 AM",
    startBlock: 9,
    endBlock: 24,
  },
];

type BlockRangeSelectorProps = {
  blocks: number[];
  placeholder: string;
  selectedOptions: number[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<number[]>>;
};

const BlockRangeSelector: React.FC<BlockRangeSelectorProps> = ({
  blocks,
  placeholder,
  selectedOptions,
  setSelectedOptions,
}) => {
  const [mounted, setMounted] = React.useState(false);
  const [isOpen, setIsOpen] = React.useState(false);
  const closeTimeoutRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [finalRange, setFinalRange] = React.useState<Record<string, unknown>>(
    {},
  );

  const openPicker = () => {
    if (closeTimeoutRef.current) clearTimeout(closeTimeoutRef.current);
    setMounted(true);
    requestAnimationFrame(() => setIsOpen(true));
  };

  const closePicker = () => {
    setIsOpen(false);
    closeTimeoutRef.current = setTimeout(() => setMounted(false), 250);
  };

  // CRITICAL FIX: Sync finalRange when selectedOptions changes externally
  React.useEffect(() => {
    if (selectedOptions.length === 0) {
      setFinalRange({});
    } else {
      // When blocks are removed externally, recalculate finalRange
      const newFinalRange: Record<string, number[]> = {};

      // Group consecutive blocks into ranges
      if (selectedOptions.length > 0) {
        const sortedBlocks = [...selectedOptions].sort((a, b) => a - b);
        let rangeStart = sortedBlocks[0];
        let rangeEnd = sortedBlocks[0];
        let currentRange = [sortedBlocks[0]];

        for (let i = 1; i < sortedBlocks.length; i++) {
          if (sortedBlocks[i] === rangeEnd + 1) {
            // Consecutive block, extend the range
            rangeEnd = sortedBlocks[i];
            currentRange.push(sortedBlocks[i]);
          } else {
            // Non-consecutive, save current range and start new one
            const rangeKey = `${rangeStart}-${rangeEnd}`;
            newFinalRange[rangeKey] = currentRange;

            rangeStart = sortedBlocks[i];
            rangeEnd = sortedBlocks[i];
            currentRange = [sortedBlocks[i]];
          }
        }

        // Save the last range
        const rangeKey = `${rangeStart}-${rangeEnd}`;
        newFinalRange[rangeKey] = currentRange;
      }

      setFinalRange(newFinalRange);
    }
  }, [selectedOptions]);

  const removeTag = (blockToRemove: number, indexToRemove: number) => {
    setSelectedOptions((prev) => {
      const newArray = prev.filter((_, index) => index !== indexToRemove);
      return newArray;
    });
  };

  const clearAllBlocks = () => {
    setSelectedOptions([]);
  };

  return (
    <div className="relative">
      <div
        className={`p-2 border border-[#e2e2e5] flex items-center justify-between gap-0 font-medium rounded-lg ${"w-[140px] h-[37px]"}`}
      >
        <div
          className="flex flex-1 justify-between items-center overflow-y-auto min-h-5 max-h-[38px] scroll-thin cursor-pointer"
          onClick={() => (mounted ? closePicker() : openPicker())}
        >
          {selectedOptions.length > 0 ? (
            <div className="flex flex-wrap gap-1 w-full">
              {selectedOptions.map((block, index) => (
                <div
                  key={`${block}-${index}`}
                  className="flex items-center gap-0 bg-gray-200 text-gray-700 text-xs px-1 py-1 rounded whitespace-nowrap flex-shrink-0"
                >
                  <span>{block}</span>
                  <button
                    type="button"
                    className="cursor-pointer hover:bg-red-400 hover:text-white rounded text-gray-500 text-sm leading-none px-1 transition-colors flex-shrink-0"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      removeTag(block, index);
                    }}
                    style={{ minWidth: "16px", minHeight: "16px" }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <span className="text-[12px] text-[#8c8a96]">{placeholder}</span>
          )}
        </div>

        <div className="flex items-center gap-1">
          {selectedOptions.length > 0 && (
            <X
              size={18}
              className="text-[#A8A29F] cursor-pointer hover:text-red-600 transition-colors"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                clearAllBlocks();
              }}
            />
          )}
          <SelectBlock
            className="w-[20px] h-[20px] cursor-pointer"
            onClick={(e: React.MouseEvent<SVGSVGElement>) => {
              e.preventDefault();
              e.stopPropagation();
              mounted ? closePicker() : openPicker();
            }}
          />
        </div>
      </div>

      {mounted &&
        ReactDOM.createPortal(
          <>
            <style>{`
              .brs-backdrop {
                transition: opacity 250ms ease;
              }
              .brs-backdrop-enter { opacity: 0; }
              .brs-backdrop-open  { opacity: 1; }

              .brs-modal {
                transition: opacity 250ms ease, transform 250ms ease;
              }
              .brs-modal-enter { opacity: 0; transform: scale(0.92) translateY(12px); }
              .brs-modal-open  { opacity: 1; transform: scale(1)    translateY(0);    }
            `}</style>

            {/* Backdrop */}
            <div
              className={`fixed inset-0 z-[9998] brs-backdrop ${isOpen ? "brs-backdrop-open" : "brs-backdrop-enter"}`}
              style={{ background: "rgba(55,57,60,0.6)", backdropFilter: "blur(2px)" }}
              onClick={closePicker}
            />

            {/* Modal */}
            <div className="fixed inset-0 z-[9999] flex items-center justify-center pointer-events-none">
              <div
                className={`pointer-events-auto brs-modal ${isOpen ? "brs-modal-open" : "brs-modal-enter"}`}
                onClick={(e) => e.stopPropagation()}
              >
                <BlockSelectionModel
                  setSelectedOptions={setSelectedOptions}
                  selectedOptions={selectedOptions}
                  blocks={blocks}
                  setOpen={closePicker}
                  setFinalRange={setFinalRange}
                  finalRange={finalRange}
                />
              </div>
            </div>
          </>,
          document.body,
        )}
    </div>
  );
};

type BlockSelectionModelProps = {
  blocks: number[];
  setSelectedOptions: React.Dispatch<React.SetStateAction<number[]>>;
  setOpen: () => void;
  selectedOptions: number[];
  finalRange: Record<string, unknown>;
  setFinalRange: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
};

function BlockSelectionModel({
  blocks,
  setSelectedOptions,
  setOpen,
  selectedOptions,
  finalRange,
  setFinalRange,
}: BlockSelectionModelProps) {
  const dispatch = useDispatch();

  return (
    <div className="transition-all border ease-in-out duration-200 w-[800px] text-sm text-[#A8A29F] h-full bg-[#FAFAFA] rounded-xl shadow-lg relative">
      <div className="flex h-full w-full">
        <div className="p-5 h-full rounded-lg w-[35%] bg-gray-50">
          <ul className="mt-2 h-full flex flex-col gap-4">
            <h5 className="text-gray-800 text-xs font-medium">
              Suggested Blocks
            </h5>
            {BlockSuggestions.map((suggestion, index) => (
              <li
                key={index}
                onClick={() => {
                  const range: number[] = [];
                  for (
                    let i = suggestion.startBlock;
                    i <= suggestion.endBlock;
                    i++
                  ) {
                    range.push(i);
                  }
                  // setSelectedOptions((prev) => [...prev, ...range]);

                  dispatch(
                    setBlockRange({
                      startBlock: suggestion.startBlock,
                      endBlock: suggestion.endBlock,
                      isClicked: true,
                    }),
                  );
                }}
                onMouseEnter={() => {
                  dispatch(
                    setBlockRange({
                      startBlock: suggestion.startBlock,
                      endBlock: suggestion.endBlock,
                    }),
                  );
                }}
                onMouseLeave={() => {
                  dispatch(setBlockRange({ startBlock: null, endBlock: null }));
                }}
                className="text-xs cursor-pointer"
              >
                <button
                  type="button"
                  className="text-start p-2 w-full hover:border-primary border border-transparent rounded-lg hover:text-primary hover:bg-primary/5 transition-colors ease-in-out duration-200"
                >
                  {suggestion.label}
                  <br />
                  <span>{suggestion.range}</span>
                </button>
              </li>
            ))}
          </ul>
          {/* NEW: Instructions banner */}
          {/* <div className="col-span-3 mb-2 p-3 bg-gray-50 border border-gray-200 rounded-lg">
            <p className="text-xs text-gray-600">
              <strong>Instructions:</strong>
              <br />
              <span className="font-medium">Range Selection:</span>
              <br /> Single-click start block, hover to preview, single-click
              end block
              <br />
              <br />
              <span className="font-medium">Individual Selection:</span>
              <br />
              Double-click any block to select/deselect it individually
            </p>
          </div> */}
        </div>

        <div className="w-[65%] h-full bg-white rounded-xl shadow-lg">
          <BlocksSelector
            key={JSON.stringify(finalRange)}
            setOpen={setOpen}
            selectedOptions={selectedOptions}
            setSelectedOptions={setSelectedOptions}
            blocks={blocks}
            finalRange={finalRange}
            setFinalRange={setFinalRange}
          />
        </div>
      </div>
    </div>
  );
}

export default BlockRangeSelector;

// import React from "react";
// import ReactDOM from "react-dom";
// import BlocksSelector from "./BlocksSelector.enhanced";
// import { useDispatch } from "react-redux";
// import { setBlockRange } from "../../store/state/BlockSuggestion";
// import { Clock, X } from "lucide-react";
// import { ReactComponent as SelectBlock } from "../../icons/Select_block.svg";

// export const TimeDivider = [
//   {
//     name: "Off-Peak",
//     range: "12:00 AM – 6:00 AM",
//     startBlock: 1,
//     endBlock: 24,
//   },
//   { name: "Daytime", range: "6:00 AM – 6:00 PM", startBlock: 25, endBlock: 72 },
//   {
//     name: "Evening + Late Night",
//     range: "6:00 PM – 12:00 AM",
//     startBlock: 73,
//     endBlock: 96,
//   },
// ];

// export const BlockSuggestions = [
//   {
//     label: "Morning Peak",
//     range: "6:00 AM – 10:00 AM",
//     startBlock: 25,
//     endBlock: 40,
//   },
//   {
//     label: "Midday Dip",
//     range: "12:00 PM – 2:00 PM",
//     startBlock: 49,
//     endBlock: 56,
//   },
//   {
//     label: "Evening Peak",
//     range: "6:00 PM – 10:00 PM",
//     startBlock: 73,
//     endBlock: 88,
//   },
//   {
//     label: "Off-Peak Night",
//     range: "2:00 AM – 6:00 AM",
//     startBlock: 9,
//     endBlock: 24,
//   },
// ];

// type BlockRangeSelectorProps = {
//   blocks: number[];
//   placeholder: string;
//   selectedOptions: number[];
//   setSelectedOptions: React.Dispatch<React.SetStateAction<number[]>>;
// };

// const BlockRangeSelector: React.FC<BlockRangeSelectorProps> = ({
//   blocks,
//   placeholder,
//   selectedOptions,
//   setSelectedOptions,
// }) => {
//   const [visible, setVisible] = React.useState(false);
//   const [animating, setAnimating] = React.useState(false);
//   const [finalRange, setFinalRange] = React.useState<Record<string, unknown>>(
//     {},
//   );

//   const openPicker = () => {
//     setVisible(true);
//     requestAnimationFrame(() => setAnimating(true));
//   };

//   const closePicker = React.useCallback(() => {
//     setAnimating(false);
//     setTimeout(() => setVisible(false), 200);
//   }, []);

//   // CRITICAL FIX: Sync finalRange when selectedOptions changes externally
//   React.useEffect(() => {
//     if (selectedOptions.length === 0) {
//       setFinalRange({});
//     } else {
//       // When blocks are removed externally, recalculate finalRange
//       const newFinalRange: Record<string, number[]> = {};

//       // Group consecutive blocks into ranges
//       if (selectedOptions.length > 0) {
//         const sortedBlocks = [...selectedOptions].sort((a, b) => a - b);
//         let rangeStart = sortedBlocks[0];
//         let rangeEnd = sortedBlocks[0];
//         let currentRange = [sortedBlocks[0]];

//         for (let i = 1; i < sortedBlocks.length; i++) {
//           if (sortedBlocks[i] === rangeEnd + 1) {
//             // Consecutive block, extend the range
//             rangeEnd = sortedBlocks[i];
//             currentRange.push(sortedBlocks[i]);
//           } else {
//             // Non-consecutive, save current range and start new one
//             const rangeKey = `${rangeStart}-${rangeEnd}`;
//             newFinalRange[rangeKey] = currentRange;

//             rangeStart = sortedBlocks[i];
//             rangeEnd = sortedBlocks[i];
//             currentRange = [sortedBlocks[i]];
//           }
//         }

//         // Save the last range
//         const rangeKey = `${rangeStart}-${rangeEnd}`;
//         newFinalRange[rangeKey] = currentRange;
//       }

//       setFinalRange(newFinalRange);
//     }
//   }, [selectedOptions]);

//   const removeTag = (blockToRemove: number, indexToRemove: number) => {
//     setSelectedOptions((prev) => {
//       const newArray = prev.filter((_, index) => index !== indexToRemove);
//       return newArray;
//     });
//   };

//   const clearAllBlocks = () => {
//     setSelectedOptions([]);
//   };

//   return (
//     <div className="relative">
//       <div
//         className={`p-2 border border-[#e2e2e5] flex items-center justify-between gap-0 font-medium rounded-lg ${
//           selectedOptions.length > 0
//             ? "w-[160px] h-[40px]"
//             : " w-[160px] h-[40px]"
//         }`}
//       >
//         <div
//           className="flex items-start flex-1 overflow-y-auto min-h-5 max-h-[38px] scroll-thin cursor-pointer"
//           onClick={() => (visible ? closePicker() : openPicker())}
//         >
//           {selectedOptions.length > 0 ? (
//             <div className="flex flex-wrap gap-1 w-full">
//               {selectedOptions.map((block, index) => (
//                 <div
//                   key={`${block}-${index}`}
//                   className="flex items-center gap-0 bg-gray-200 text-gray-700 text-xs px-1 py-1 rounded whitespace-nowrap flex-shrink-0"
//                 >
//                   <span>{block}</span>
//                   <button
//                     type="button"
//                     className="cursor-pointer hover:bg-red-400 hover:text-white rounded text-gray-500 text-sm leading-none px-1 transition-colors flex-shrink-0"
//                     onClick={(e) => {
//                       e.preventDefault();
//                       e.stopPropagation();
//                       removeTag(block, index);
//                     }}
//                     style={{ minWidth: "16px", minHeight: "16px" }}
//                   >
//                     ×
//                   </button>
//                 </div>
//               ))}
//             </div>
//           ) : (
//             <span className="text-[13px] px-2 py-2 text-[#8c8a96]">
//               {placeholder}
//             </span>
//           )}
//         </div>

//         <div className="flex items-center gap-1">
//           {selectedOptions.length > 0 && (
//             <X
//               size={18}
//               className="text-[#A8A29F] cursor-pointer hover:text-red-600 transition-colors"
//               onClick={(e) => {
//                 e.preventDefault();
//                 e.stopPropagation();
//                 clearAllBlocks();
//               }}
//             />
//           )}
//           <SelectBlock
//             className="w-[23px] h-[23px] cursor-pointer"
//             onClick={(e: React.MouseEvent<SVGSVGElement>) => {
//               e.preventDefault();
//               e.stopPropagation();
//               visible ? closePicker() : openPicker();
//             }}
//           />
//         </div>
//       </div>

//       {visible &&
//         ReactDOM.createPortal(
//           <div
//             className={`fixed inset-0 z-[9999] flex items-center justify-center transition-all duration-200 ${
//               animating ? "bg-black/40 backdrop-blur-[2px]" : "bg-transparent"
//             }`}
//             onClick={closePicker}
//           >
//             <div
//               className={`pointer-events-auto transition-all duration-200 ${
//                 animating
//                   ? "opacity-100 scale-100 translate-y-0"
//                   : "opacity-0 scale-95 translate-y-2"
//               }`}
//               onClick={(e) => e.stopPropagation()}
//             >
//               <BlockSelectionModel
//                 setSelectedOptions={setSelectedOptions}
//                 selectedOptions={selectedOptions}
//                 blocks={blocks}
//                 setOpen={closePicker}
//                 setFinalRange={setFinalRange}
//                 finalRange={finalRange}
//               />
//             </div>
//           </div>,
//           document.body,
//         )}
//     </div>
//   );
// };

// type BlockSelectionModelProps = {
//   blocks: number[];
//   setSelectedOptions: React.Dispatch<React.SetStateAction<number[]>>;
//   setOpen: () => void;
//   selectedOptions: number[];
//   finalRange: Record<string, unknown>;
//   setFinalRange: React.Dispatch<React.SetStateAction<Record<string, unknown>>>;
// };

// function BlockSelectionModel({
//   blocks,
//   setSelectedOptions,
//   setOpen,
//   selectedOptions,
//   finalRange,
//   setFinalRange,
// }: BlockSelectionModelProps) {
//   const dispatch = useDispatch();
//   const [activePreset, setActivePreset] = React.useState<string | null>(null);

//   return (
//     <div className="w-[650px] bg-white rounded-xl shadow-2xl overflow-hidden">
//       {/* Header */}
//       <div className="flex w-full items-center justify-between px-5 py-3.5 border-b border-gray-100">
//         <div className="flex items-center gap-3">
//           <div
//             className="flex items-center justify-center w-9 h-9 rounded-xl"
//             style={{ backgroundColor: "rgba(241, 147, 92, 0.08)" }}
//           >
//             <Clock size={17} color="#F1935C" strokeWidth={1.8} />
//           </div>
//           <div>
//             <h3 className="text-[13px] font-semibold text-gray-600 leading-tight">
//               Select Time Blocks
//             </h3>
//             <p className="text-[11px] text-gray-400 mt-0.5">
//               Single-click to set range · Double-click for individual block
//             </p>
//           </div>
//         </div>
//         <button
//           onClick={setOpen}
//           className="flex items-center justify-center w-8 h-8 rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors"
//           title="Close"
//         >
//           <X size={15} className="text-gray-400" />
//         </button>
//       </div>

//       {/* Quick select pills */}
//       <div className="flex items-center gap-1.5 px-5 py-2.5 border-b border-gray-100 bg-gray-50/50">
//         {BlockSuggestions.map((suggestion) => {
//           const isActive = activePreset === suggestion.label;
//           return (
//             <button
//               key={suggestion.label}
//               onClick={() => {
//                 setActivePreset(suggestion.label);
//                 dispatch(
//                   setBlockRange({
//                     startBlock: suggestion.startBlock,
//                     endBlock: suggestion.endBlock,
//                     isClicked: true,
//                   }),
//                 );
//               }}
//               onMouseEnter={() => {
//                 dispatch(
//                   setBlockRange({
//                     startBlock: suggestion.startBlock,
//                     endBlock: suggestion.endBlock,
//                   }),
//                 );
//               }}
//               onMouseLeave={() => {
//                 dispatch(setBlockRange({ startBlock: null, endBlock: null }));
//               }}
//               className={`px-3 py-[5px] text-[11px] font-medium rounded-lg transition-all duration-150 whitespace-nowrap ${
//                 isActive
//                   ? "text-white shadow-sm"
//                   : "text-gray-500 hover:text-gray-700 hover:bg-white hover:shadow-sm"
//               }`}
//               style={isActive ? { backgroundColor: "#F1935C" } : undefined}
//             >
//               {suggestion.label}
//             </button>
//           );
//         })}
//       </div>

//       {/* Block grid */}
//       <div className="w-full bg-white">
//         <BlocksSelector
//           key={JSON.stringify(finalRange)}
//           setOpen={setOpen}
//           selectedOptions={selectedOptions}
//           setSelectedOptions={setSelectedOptions}
//           blocks={blocks}
//           finalRange={finalRange}
//           setFinalRange={setFinalRange}
//           onClearPreset={() => setActivePreset(null)}
//         />
//       </div>
//     </div>
//   );
// }

// export default BlockRangeSelector;
